import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Fab,
  Tabs,
  Tab,
  Container,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  VideoLibrary as VideoIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingIcon,
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { campaignService, handleFirebaseError } from '../../services/firebaseService';

export default function Campaigns({ user }) {
  const [campaigns, setCampaigns] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('all');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [joinedCampaigns, setJoinedCampaigns] = useState([]);
  const [newCampaign, setNewCampaign] = useState({
    title: '',
    description: '',
    budget: '',
    duration: '',
    requirements: '',
    category: 'General',
    perClip: '25'
  });

  useEffect(() => {
    if (user?.uid) {
      loadCampaigns();
      loadJoinedCampaigns();
    }
  }, [user]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const allCampaigns = await campaignService.getCampaigns({ status: 'active' });
      setCampaigns(allCampaigns);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setError(handleFirebaseError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadJoinedCampaigns = async () => {
    try {
      const joined = await campaignService.getUserJoinedCampaigns(user.uid);
      setJoinedCampaigns(joined.map(c => c.id));
    } catch (error) {
      console.error('Error loading joined campaigns:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setDialogOpen(true);
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'paused':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getDaysRemaining = (deadline) => {
    if (!deadline) return 0;
    const deadlineDate = deadline?.toDate ? deadline.toDate() : new Date(deadline);
    const days = Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const handleCreateCampaign = () => {
    setCreateDialogOpen(true);
  };

  const handleSubmitCampaign = async () => {
    if (!newCampaign.title || !newCampaign.description || !newCampaign.budget) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const campaignData = {
        title: newCampaign.title,
        description: newCampaign.description,
        budget: parseFloat(newCampaign.budget),
        perClip: parseFloat(newCampaign.perClip) || 25,
        category: newCampaign.category,
        duration: parseInt(newCampaign.duration) || 7,
        requirements: newCampaign.requirements.split('\n').filter(req => req.trim()),
        createdBy: user.uid,
        creatorName: user.displayName || user.email,
        status: 'active',
        maxSubmissions: Math.floor(parseFloat(newCampaign.budget) / parseFloat(newCampaign.perClip)),
        participants: 0,
        submissions: 0
      };

      await campaignService.createCampaign(campaignData);
      setSuccess('Campaign created successfully!');
      setCreateDialogOpen(false);
      setNewCampaign({
        title: '',
        description: '',
        budget: '',
        duration: '',
        requirements: '',
        category: 'General',
        perClip: '25'
      });
      loadCampaigns(); // Reload campaigns
    } catch (error) {
      console.error('Error creating campaign:', error);
      setError(handleFirebaseError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinCampaign = async (campaignId) => {
    try {
      setSubmitting(true);
      await campaignService.joinCampaign(campaignId, user.uid);
      setJoinedCampaigns([...joinedCampaigns, campaignId]);
      setSuccess('Successfully joined campaign!');
      loadCampaigns(); // Reload to update participant count
    } catch (error) {
      console.error('Error joining campaign:', error);
      setError(handleFirebaseError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeaveCampaign = async (campaignId) => {
    try {
      setSubmitting(true);
      await campaignService.leaveCampaign(campaignId, user.uid);
      setJoinedCampaigns(joinedCampaigns.filter(id => id !== campaignId));
      setSuccess('Left campaign successfully');
      loadCampaigns(); // Reload to update participant count
    } catch (error) {
      console.error('Error leaving campaign:', error);
      setError(handleFirebaseError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const filteredAndSortedCampaigns = campaigns
    .filter(campaign => {
      const matchesTab = (() => {
        switch (tabValue) {
          case 0: return campaign.status === 'active';
          case 1: return campaign.createdBy === user?.uid; // My campaigns
          case 2: return joinedCampaigns.includes(campaign.id); // Joined campaigns
          default: return true;
        }
      })();

      const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           campaign.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter = filterBy === 'all' || campaign.category.toLowerCase() === filterBy.toLowerCase();

      return matchesTab && matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest': 
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        case 'budget_high': return b.budget - a.budget;
        case 'budget_low': return a.budget - b.budget;
        case 'deadline': 
          const deadlineA = a.deadline?.toDate ? a.deadline.toDate() : new Date(a.deadline);
          const deadlineB = b.deadline?.toDate ? b.deadline.toDate() : new Date(b.deadline);
          return deadlineA - deadlineB;
        default: return 0;
      }
    });

  const CampaignCard = ({ campaign }) => (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      borderRadius: 2,
      boxShadow: 2,
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4
      }
    }}>
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
            {campaign.title}
          </Typography>
          <Chip 
            label={campaign.status} 
            color={getStatusColor(campaign.status)}
            size="small"
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.4 }}>
          {campaign.description.length > 100 
            ? `${campaign.description.substring(0, 100)}...` 
            : campaign.description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={campaign.category} variant="outlined" size="small" />
          <Chip 
            label={`${getDaysRemaining(campaign.deadline)} days left`} 
            color={getDaysRemaining(campaign.deadline) < 3 ? 'error' : 'default'}
            size="small" 
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <MoneyIcon sx={{ fontSize: 16, mr: 0.5 }} />
              ${campaign.perClip} per clip
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {campaign.participants}
            </Typography>
          </Box>
          
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Progress: {campaign.submissions}/{campaign.maxSubmissions}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(campaign.submissions / campaign.maxSubmissions) * 100}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: '#ff5252' }}>
            {campaign.creator.name.charAt(0)}
          </Avatar>
          <Typography variant="caption" color="text.secondary">
            by {campaign.creator.name}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
        <Button 
          size="small" 
          startIcon={<ViewIcon />}
          onClick={() => handleViewCampaign(campaign)}
          sx={{ color: '#666' }}
        >
          Details
        </Button>
        
        {campaign.isCreator ? (
          <Button 
            size="small" 
            startIcon={<EditIcon />}
            color="primary"
            variant="outlined"
          >
            Manage
          </Button>
        ) : campaign.isParticipant ? (
          <Button 
            size="small" 
            startIcon={<VideoIcon />}
            color="success"
            variant="contained"
          >
            Submit
          </Button>
        ) : (
          <Button 
            size="small" 
            startIcon={<PlayIcon />}
            variant="contained"
            onClick={() => handleJoinCampaign(campaign.id)}
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
          >
            Join
          </Button>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Campaigns
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateCampaign}
          sx={{ 
            bgcolor: '#ff5252', 
            '&:hover': { bgcolor: '#ff3939' },
            borderRadius: 2,
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          Create Campaign
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="gaming">Gaming</MenuItem>
                <MenuItem value="fitness">Fitness</MenuItem>
                <MenuItem value="travel">Travel</MenuItem>
                <MenuItem value="tech">Tech</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="budget_high">Budget (High)</MenuItem>
                <MenuItem value="budget_low">Budget (Low)</MenuItem>
                <MenuItem value="deadline">Deadline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs Section */}
      <Paper sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1.1rem',
              minHeight: 48
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="All Active" />
          <Tab label="My Campaigns" />
          <Tab label="Joined Campaigns" />
        </Tabs>
      </Paper>

      {/* Campaigns Grid */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {filteredAndSortedCampaigns.map((campaign) => (
          <Grid item xs={12} sm={6} lg={4} key={campaign.id}>
            <CampaignCard campaign={campaign} />
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {filteredAndSortedCampaigns.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No campaigns found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tabValue === 1 ? 'You haven\'t created any campaigns yet.' : 
             tabValue === 2 ? 'You haven\'t joined any campaigns yet.' :
             'No active campaigns match your search criteria.'}
          </Typography>
          {tabValue === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCampaign}
              sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
            >
              Create Your First Campaign
            </Button>
          )}
        </Paper>
      )}

      {/* Create Campaign Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1.5rem', fontWeight: 600, color: 'text.primary' }}>
          Create New Campaign
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Please fill out the details below to launch your campaign.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Campaign Title"
                  value={newCampaign.title}
                  onChange={(e) => setNewCampaign({...newCampaign, title: e.target.value})}
                  placeholder="e.g. Gaming Highlights Compilation"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VideoIcon color="primary" />
                      </InputAdornment>
                    )
                  }}
                  helperText="Give your campaign a catchy, descriptive title."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                  placeholder="Describe what type of content you're looking for"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EditIcon color="primary" />
                      </InputAdornment>
                    )
                  }}
                  helperText="Explain your campaign's purpose and what you expect from participants."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Budget"
                  type="number"
                  value={newCampaign.budget}
                  onChange={(e) => setNewCampaign({...newCampaign, budget: e.target.value})}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end"><MoneyIcon color="success" /></InputAdornment>
                  }}
                  helperText="Total amount you want to spend on this campaign."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (days)"
                  type="number"
                  value={newCampaign.duration}
                  onChange={(e) => setNewCampaign({...newCampaign, duration: e.target.value})}
                  placeholder="7"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><ScheduleIcon color="primary" /></InputAdornment>
                  }}
                  helperText="How many days will your campaign run?"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={newCampaign.category}
                    label="Category"
                    onChange={(e) => setNewCampaign({...newCampaign, category: e.target.value})}
                  >
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Gaming">Gaming</MenuItem>
                    <MenuItem value="Fitness">Fitness</MenuItem>
                    <MenuItem value="Travel">Travel</MenuItem>
                    <MenuItem value="Tech">Tech</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Per Clip Reward"
                  type="number"
                  value={newCampaign.perClip}
                  onChange={(e) => setNewCampaign({...newCampaign, perClip: e.target.value})}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end"><TrendingIcon color="primary" /></InputAdornment>
                  }}
                  helperText="How much will you pay per accepted clip?"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Requirements"
                  value={newCampaign.requirements}
                  onChange={(e) => setNewCampaign({...newCampaign, requirements: e.target.value})}
                  placeholder={"List your requirements (one per line)\nExample:\n- High quality 1080p minimum\n- 30-60 seconds long\n- Good lighting and audio"}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CheckIcon color="success" /></InputAdornment>
                  }}
                  helperText="List all requirements for submissions, one per line."
                />
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Cancel</Button>
          <Button 
            onClick={handleSubmitCampaign}
            variant="contained"
            disabled={!newCampaign.title || !newCampaign.description || !newCampaign.budget}
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Campaign Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCampaign && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">{selectedCampaign.title}</Typography>
                <Chip 
                  label={selectedCampaign.status} 
                  color={getStatusColor(selectedCampaign.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedCampaign.description}
                  </Typography>
                  
                  <Typography variant="h6" sx={{ mb: 1 }}>Requirements:</Typography>
                  <List dense>
                    {selectedCampaign.requirements.map((req, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={`• ${req}`} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Campaign Stats</Typography>
                    <List dense>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Budget" 
                          secondary={`$${selectedCampaign.budget}`} 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Per Clip" 
                          secondary={`$${selectedCampaign.perClip}`} 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Participants" 
                          secondary={selectedCampaign.participants} 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Submissions" 
                          secondary={`${selectedCampaign.submissions}/${selectedCampaign.maxSubmissions}`} 
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText 
                          primary="Deadline" 
                          secondary={selectedCampaign.deadline.toLocaleDateString()} 
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Close</Button>
              {selectedCampaign.isCreator ? (
                <Button variant="contained" startIcon={<EditIcon />}>
                  Edit Campaign
                </Button>
              ) : selectedCampaign.isParticipant ? (
                <Button variant="contained" startIcon={<VideoIcon />} color="success">
                  Submit Clip
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  startIcon={<PlayIcon />}
                  onClick={() => handleJoinCampaign(selectedCampaign.id)}
                  sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#ff3939' } }}
                >
                  Join Campaign
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Floating Action Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleCreateCampaign}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          bgcolor: '#ff5252',
          '&:hover': { bgcolor: '#ff3939' },
          display: { xs: 'flex', sm: 'none' }
        }}
      >
        <AddIcon />
      </Fab>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
}
